import { Title as DialogTitle } from "@radix-ui/react-dialog";
import { FaCrown, FaCheck, FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { Modal } from "components/common/modal";
import { Button } from "components/common";
import { PATH_DASHBOARD } from "config";
import { UpgradeModalProps } from "hooks/useWorkspace/workspace.types";

const RIVET_HELP_CENTER = "https://www.help.rivetai.com/en";

/**
 * Upgrade Modal Component
 *
 * A modal that displays upgrade information and prompts users to upgrade their plan.
 * Shows current vs required plan, features unlocked, and upgrade action.
 *
 * @example
 * ```tsx
 * <UpgradeModal
 *   isOpen={showUpgradeModal}
 *   onClose={() => setShowUpgradeModal(false)}
 *   upgradeInfo={{
 *     message: "Upgrade to Pro to manage workspace members",
 *     action: "upgrade",
 *     requiredPlan: "Pro",
 *     currentPlan: "Free",
 *     features: ["Invite team members", "Manage member roles", "Remove members"]
 *   }}
 * />
 * ```
 */
export const UpgradeModal = ({ isOpen, onClose, upgradeInfo }: UpgradeModalProps) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate(PATH_DASHBOARD.selectPlan);
    onClose();
  };

  const handleContactAdmin = () => {
    window.open(RIVET_HELP_CENTER, "_blank", "noopener,noreferrer");
    onClose();
  };

  const handleRequestAccess = () => {
    window.open(RIVET_HELP_CENTER, "_blank", "noopener,noreferrer");
    onClose();
  };

  const getActionButton = () => {
    switch (upgradeInfo.action) {
      case "upgrade":
        return (
          <Button onClick={handleUpgrade} className="flex items-center gap-2">
            <FaCrown className="w-4 h-4" />
            Upgrade to {upgradeInfo.requiredPlan}
          </Button>
        );
      case "contact_admin":
        return (
          <Button onClick={handleContactAdmin} color="gray">
            Contact Admin
          </Button>
        );
      case "request_access":
        return (
          <Button onClick={handleRequestAccess} color="gray">
            Request Access
          </Button>
        );
      default:
        return <Button onClick={handleUpgrade}>Get Access</Button>;
    }
  };

  const getActionDescription = () => {
    switch (upgradeInfo.action) {
      case "upgrade":
        return "Upgrade your plan to unlock this feature and more.";
      case "contact_admin":
        return "Contact your workspace admin to request access to this feature.";
      case "request_access":
        return "Request access from your workspace admin or owner.";
      default:
        return "Get access to this feature.";
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
            <FaCrown className="w-6 h-6 text-foreground" />
          </div>
          <div>
            <DialogTitle className="text-xl font-semibold text-foreground">
              {upgradeInfo.action === "upgrade" ? "Upgrade Required" : "Access Required"}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{getActionDescription()}</p>
          </div>
        </div>

        {/* Message */}
        <div className="mb-6 rounded-lg bg-muted p-4">
          <p className="font-medium text-foreground">{upgradeInfo.message}</p>
        </div>

        {/* Plan Comparison */}
        {upgradeInfo.requiredPlan && upgradeInfo.currentPlan && (
          <div className="mb-6">
            <h3 className="mb-3 font-semibold text-foreground">Plan Comparison</h3>
            <div className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-muted-foreground"></div>
                <div>
                  <p className="font-medium text-foreground">Current: {upgradeInfo.currentPlan}</p>
                  <p className="text-sm text-muted-foreground">Limited access</p>
                </div>
              </div>
              <FaArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <div>
                  <p className="font-medium text-foreground">
                    Required: {upgradeInfo.requiredPlan}
                  </p>
                  <p className="text-sm text-muted-foreground">Full access</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        {upgradeInfo.features && upgradeInfo.features.length > 0 && (
          <div className="mb-6">
            <h3 className="mb-3 font-semibold text-foreground">
              {upgradeInfo.action === "upgrade" ? "You'll unlock:" : "This feature includes:"}
            </h3>
            <ul className="space-y-2">
              {upgradeInfo.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <FaCheck className="w-4 h-4 text-success flex-shrink-0" />
                  <span className="text-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {getActionButton()}
        </div>
      </div>
    </Modal>
  );
};

/**
 * Simple Upgrade Modal
 *
 * A simplified version for quick upgrade prompts without detailed feature lists.
 */
interface SimpleUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  actionText?: string;
  onAction?: () => void;
}

export const SimpleUpgradeModal = ({
  isOpen,
  onClose,
  title,
  message,
  actionText = "Upgrade",
  onAction,
}: SimpleUpgradeModalProps) => {
  const navigate = useNavigate();
  const handleAction = () => {
    if (onAction) {
      onAction();
    } else {
      navigate(PATH_DASHBOARD.selectPlan);
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
          <FaCrown className="w-8 h-8 text-foreground" />
        </div>

        <DialogTitle className="mb-2 text-xl font-semibold text-foreground">{title}</DialogTitle>

        <p className="mb-6 text-muted-foreground">{message}</p>

        <div className="flex gap-3 justify-center">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleAction} className="flex items-center gap-2">
            <FaCrown className="w-4 h-4" />
            {actionText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
